'use strict';

import DxfParser from 'dxf-parser';
import { cn_vector_data, cn_vector_layer, cn_vector_bloc, cn_vector_path, cn_vector_instance, cn_vector_arc, cn_vector_text } from '../model/cn_vector_data';
import {cn_polar, cn_sub}  from './cn_utilities';

//***********************************************************************************
//***********************************************************************************
//**** cn_dxf_parser converter :
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//**** Global functions :
//***********************************************************************************

export function cn_dxf_to_json(serialized_dxf_file) {
    const parser = new DxfParser();
    const dxf = parser.parse(serialized_dxf_file);

    const insUnitConvert = defineUnitConvert(dxf.header.$INSUNITS);
    const scale = +dxf.header.$LTSCALE || 1;
    
    /** Scan layers */
    let vector_data = new cn_vector_data(null);
    for (let layerName in dxf.tables.layer.layers) {
        const layer = dxf.tables.layer.layers[layerName];
        let vector_layer = new cn_vector_layer(vector_data);
        vector_data.layers.push(vector_layer);
        vector_layer.label = layerName;
        vector_layer.color = getConstrastedColorForWhiteBg(layer.color.toString(16).padStart(6, '0'));
        vector_layer.visible = true;
    }

    /** read blocks */
    for (var p in dxf.blocks) {
        let block = dxf.blocks[p];
        if (block.entities && block.entities.length) {
            let vec = new cn_vector_bloc(vector_data);
            vec.name = block.name;
            vec.position = [(block.position.x || 0) * insUnitConvert/scale,(block.position.y || 0)* insUnitConvert/scale];
            block.entities.forEach(ent => {
                let vector_element = convert_dxf_entity(ent,insUnitConvert,scale,vec);
                if (vector_element) vec.elements.push(vector_element);
            });
            vector_data.blocs.push(vec);
            vec["original_block"] = block;
        }
    };

    //*** Read entities */
    let currentLayer = null;
    dxf.entities
        .filter((ent) => isEntityAccepted(ent))
        .sort((entA, entB) => entA.layer.localeCompare(entB.layer))
        .forEach((ent) => {
            if (!currentLayer || currentLayer.label != ent.layer)
                currentLayer = vector_data.layers.find(l => l.label == ent.layer);
            if (!currentLayer) return;
            
            let vector_element = convert_dxf_entity(ent,insUnitConvert,scale,currentLayer);
            if (!vector_element) return;
            if (ent.type == 'INSERT') {
                // @ts-ignore
                let bloc = vector_data.blocs.find(b => b.name == vector_element.name);
                if (!bloc) return;
            }
            currentLayer.elements.push(vector_element);
        });
        
    vector_data.layers = vector_data.layers.filter(l => l.elements.length > 0);

    return vector_data;
}

function convert_dxf_entity(ent, conversion, scale, layer, do_inserts = true) {

    let color = "";
    if (ent.colorIndex == 7)
        color = "revert";
    else if (ent.colorIndex>0 && ent.colorIndex<256)
        color = getConstrastedColorForWhiteBg(ent.color.toString(16).padStart(6, '0'));
    else if (ent.colorIndex == 0) 
        color = "currentColor";

    if (ent.type === 'LINE' || ent.type === 'LWPOLYLINE' || ent.type === 'POLYLINE') {
        let vec = new cn_vector_path(layer);
        vec.color = color;
        vec.closed_contour = ent.shape || false;
        vec.vertices = ent.vertices.map(ev => [ev.x * conversion/scale, ev.y * conversion/scale]);
        return vec;
    } 

    if (ent.type === 'ARC' || ent.type === 'CIRCLE') {
        let vec = new cn_vector_arc(layer);
        vec.color = color;
        vec.center = [ent.center.x *  conversion / scale, ent.center.y *  conversion / scale];
        vec.radius[0] = vec.radius[1] = ent.radius * conversion / scale;
        vec.start_angle = 0;
        vec.end_angle = 2 * Math.PI;
        if (
            ent.type !== 'CIRCLE' &&
            ent.startAngle !== undefined &&
            ent.endAngle !== undefined
        ) {
            vec.start_angle = ent.startAngle;
            vec.end_angle = ent.endAngle;
        }
        return vec;
    } 
    
    if (do_inserts && ent.type === 'ELLIPSE') {
        let vec = new cn_vector_arc(layer);
        vec.color = color;
        vec.center = [ent.center.x *  conversion / scale, ent.center.y *  conversion / scale];
        vec.radius[0] = ent.radius * conversion / scale;
        vec.radius[1] = vec.radius[0] * ent.axisRatio;
        vec.start_angle = ent.startAngle || 0;
        vec.end_angle = ent.endAngle || 2 * Math.PI;
        let majaxis = [ent.majorAxisEndPoint.x *  conversion / scale, ent.majorAxisEndPoint.y *  conversion / scale];
        vec.angle = cn_polar(cn_sub(majaxis,vec.center))[1];
        return vec;
    }

    if (ent.type === 'INSERT') {
        let vec = new cn_vector_instance(layer);
        vec.color = color;
        vec.name = ent.name;
        vec.position = [ent.position.x *  conversion / scale, ent.position.y *  conversion / scale];
        vec.scale = [ent.xScale || 1, ent.yScale || 1];
        vec.rotation = ent.rotation || 0;
        return vec;
    }

    if (ent.type === 'TEXT') {
        if (!ent.startPoint) return;
        
        let vec = new cn_vector_text(layer);
        vec.color = color;
        
        vec.align = 0;
        vec.position= [ent.startPoint.x* conversion / scale,ent.startPoint.y* conversion / scale];
        if (ent.endPoint) {
            if (ent.halign == 3 || ent.halign == 5) {
                vec.position= [(ent.startPoint.x + ent.endPoint.x)* conversion / scale,(ent.startPoint.y + ent.endPoint.y)* conversion / scale];
                vec.align = 1;
            }
            else if (ent.halign == 1 || ent.halign == 2 || ent.halign == 4 ) {
                vec.position= [ent.endPoint.x* conversion / scale,ent.endPoint.y* conversion / scale];
                vec.align = (ent.halign == 2)?2:1;
            }
        }
        
        vec.text = ent.text;

        vec.rotation = ent.rotation || 0;
        vec.height = (ent.height || ent.textHeight || 6) * conversion / scale;

        return vec;
    }

    if (ent.type === 'MTEXT') {
        if (!ent.position) return;
        let vec = new cn_vector_text(layer);
        vec.color = color;
        
        vec.position= [ent.position.x* conversion / scale,ent.position.y* conversion / scale];
        let attach = ent.attachmentPoint || 5;
        if (attach == 1 || attach==4 || attach == 7)
            vec.align = 0;
        else if (attach == 3 || attach==6 || attach == 9)
            vec.align = 2;
        else 
            vec.align = 1;
        
        vec.text = ent.text;//cleanMText(ent.text);

        vec.rotation = ent.rotation || 0;
        if (ent.directionVector)
            vec.rotation = 180 * cn_polar([ent.directionVector.x,ent.directionVector.y])[1] / Math.PI;

        vec.height = (ent.height || ent.textHeight || 6) * conversion / scale;

        return vec;
    }


    console.log("unknown entity",ent.type,ent);

    return null;
}

function defineUnitConvert(unitSource) {
    let result = 1;
    if (unitSource !== 6 && unitSource >= 1 && unitSource <= 7) {
        // Inch, feet, mile
        const english = [1, 2, 3];
        // mm, cm, m, km
        const metricMul = [1, 10, 1000, 10000];
        const englishMul = [1, 12, 63360];
        const inchToMM = 25.4;
        if (english.includes(unitSource)) {
            const toInch = englishMul[unitSource - 1];
            result = (toInch * inchToMM) / 1000;
        } else {
            result = metricMul[unitSource - 4] / 1000;
        }
    }
    return result;
}

function getConstrastedColorForWhiteBg(rawColor) {
    let r = parseInt(rawColor.slice(0, 2), 16);
    let g = parseInt(rawColor.slice(2, 4), 16);
    let b = parseInt(rawColor.slice(4), 16);

    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    return `#${[r,g,b].map(code => code.toString(16).padStart(2, '0')).join('')}`
}

function isEntityAccepted(ent) {
    return (
        !ent.inPaperSpace &&
        !ent.elevation &&
        (ent.visible === undefined || ent.visible === true)
    );
}
